import { useEffect, useState } from "react";
import { X, ScrollText, ChevronDown } from "lucide-react";
import { renderMarkdownBody } from "@/lib/simpleMarkdown";
import { useScrollCue } from "@/hooks/useScrollCue";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * iter87.199.145 — Pro+ Billing Terms modal.
 *
 * Previously the "Pro+ Billing Terms" links (Subscribe checkbox +
 * Landing footer) opened `/terms/pro-plus-billing` in a new tab,
 * inconsistent with how the general Terms of Use / Privacy Notice
 * links open in-place modals. This fetches the same live markdown
 * (`GET /api/billing/paddle/terms`) and renders it in a modal that
 * visually matches `TermsModal.jsx` / `PrivacyModal.jsx`.
 *
 * The standalone `/terms/pro-plus-billing` route (`ProPlusBillingTerms.jsx`)
 * still exists for direct links / crawlers — this modal is purely an
 * in-flow convenience so users don't lose their place.
 * iter87.199.146 — Added a "more below" scroll cue.
 */
export default function ProPlusBillingTermsModal({ open, onClose }) {
    const [state, setState] = useState({ loading: true, error: null, md: "", version: "" });
    const [bodyRef, showScrollCue] = useScrollCue([open, state.loading, state.md]);

    useEffect(() => {
        if (!open) return;
        setState({ loading: true, error: null, md: "", version: "" });
        fetch(`${API}/api/billing/paddle/terms`)
            .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then((j) => setState({ loading: false, error: null, md: j.markdown, version: j.version }))
            .catch((e) => setState({ loading: false, error: e.message, md: "", version: "" }));
    }, [open]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            data-testid="pro-plus-billing-terms-modal-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="brutal-card max-w-2xl w-full max-h-[85dvh] flex flex-col"
                data-testid="pro-plus-billing-terms-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 p-5 border-b-2 border-[#222]">
                    <div className="flex items-center gap-2 min-w-0">
                        <ScrollText className="w-5 h-5 text-cyber shrink-0" />
                        <h2 className="font-display font-black text-xl uppercase truncate">Pro+ Billing Terms</h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="brutal-btn-ghost p-1.5"
                        data-testid="pro-plus-billing-terms-modal-close-btn"
                        aria-label="Close Pro+ Billing Terms"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div
                    ref={bodyRef}
                    className="overflow-y-auto px-5 py-3 flex-1 space-y-2"
                    data-testid="pro-plus-billing-terms-modal-body"
                >
                    {state.loading && (
                        <div className="text-white/60 text-sm">Loading terms…</div>
                    )}
                    {state.error && (
                        <div className="text-red-400 text-sm" data-testid="pro-plus-billing-terms-modal-error">
                            Could not load terms ({state.error}). Please try again or contact admin@tinycrewrivalries.com.
                        </div>
                    )}
                    {!state.loading && !state.error && renderMarkdownBody(state.md)}
                    {showScrollCue && (
                        <div
                            className="sticky bottom-0 -mx-5 -mb-3 h-10 bg-gradient-to-t from-[#121212] to-transparent flex items-end justify-center pb-1 pointer-events-none"
                            data-testid="pro-plus-billing-terms-modal-scroll-cue"
                        >
                            <ChevronDown className="w-4 h-4 text-white/50 animate-bounce" />
                        </div>
                    )}
                </div>
                <div className="p-3 border-t-2 border-[#222] bg-ink flex items-center justify-between">
                    <p className="text-[10px] text-zinc-600 font-mono">Version {state.version || "—"}</p>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="pro-plus-billing-terms-modal-close-footer-btn"
                        className="brutal-btn-ghost text-xs"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
